import classNames from 'classnames';
import * as _ from 'lodash-es';
import React, { useRef, useState } from 'react';
import SVGIcon from '../../../components/SVGIcon';
import { MOD_KEY } from '../../../constants/os';
import dragIcon from '../../../icons/non-prefilled/16x16/tableDrag.svg';
import addTestId from '../../../utils/addTestId';
import useEventListener from '../../../utils/useEventListener';
import { CELL_IDENTITY_ATTRIBUTE } from '../constants';
import type { RearrangingState } from '../types';
import { parseCellIdentity } from '../utils';
import FakeSelectionButton from './FakeSelectionButton';
import * as styles from './RowColumnControl.module.scss';

export enum SelectTypes {
  Single = 'SINGLE',
  Multiple = 'MULTIPLE',
  Consecutive = 'CONSECUTIVE',
}

const getSelectTypeFromClickEvent = (
  clickEvent: React.MouseEvent,
): SelectTypes => {
  if (clickEvent.shiftKey) return SelectTypes.Consecutive;
  if (clickEvent[MOD_KEY]) return SelectTypes.Multiple;
  return SelectTypes.Single;
};

interface RowColumnControlProps {
  direction: 'row' | 'column';
  index: number;
  last: boolean;
  rearrangingState: RearrangingState | null;
  getIsSelected: (index: number) => boolean;
  onSelect: (
    index: number,
    options: { isRow: boolean; selectType: SelectTypes },
  ) => void;
  onRearrangingStateChange: (state: RearrangingState | null) => void;
  onRearrange: (from: number, to: number, isRow: boolean) => void;
}

interface DraggingState {
  offset?: number;
  initialOffset: number;
  style: {
    width: number;
    height: number;
  };
}

const RowColumnControl = ({
  direction,
  index,
  last,
  rearrangingState,
  getIsSelected,
  onSelect,
  onRearrangingStateChange,
  onRearrange,
}: RowColumnControlProps) => {
  const ref = useRef<HTMLButtonElement>(null);
  const [dragging, setDragging] = useState<DraggingState | null>(null);

  useEventListener(
    window,
    'mousemove',
    (e: MouseEvent) => {
      if (!ref.current || e.metaKey || e.shiftKey) return;
      const tableEl = ref.current.closest('table');
      if (!tableEl) return;
      const rect = ref.current.getBoundingClientRect();
      const tableRect = tableEl.getBoundingClientRect();

      setDragging((value) => {
        if (!value) return value;
        const mainProperty = direction === 'row' ? 'top' : 'left';
        const offset =
          (direction === 'row' ? e.clientY : e.clientX) - rect[mainProperty];

        return {
          ...value,
          offset: _.clamp(
            offset,
            tableRect[mainProperty] - rect[mainProperty] - value.initialOffset,
            tableRect[mainProperty] +
              tableRect[direction === 'row' ? 'height' : 'width'] -
              value.initialOffset -
              rect[mainProperty] -
              value.style[direction === 'row' ? 'height' : 'width'],
          ),
        };
      });
    },
    { skip: !dragging, options: { passive: true } },
  );

  useEventListener(
    window,
    'mouseover',
    (e: MouseEvent) => {
      if (e.metaKey || e.shiftKey) return;
      if (e.target instanceof HTMLElement) {
        const closestCell = e.target.closest(`[${CELL_IDENTITY_ATTRIBUTE}]`);
        const closestTable = closestCell?.closest('table');
        if (!closestTable || closestTable !== ref.current?.closest('table')) {
          return;
        }
        const identity = closestCell?.getAttribute(CELL_IDENTITY_ATTRIBUTE);
        const parsed = identity && parseCellIdentity(identity);
        if (parsed) {
          const isRow = direction === 'row';
          onRearrangingStateChange({
            isRow,
            to: parsed[isRow ? 0 : 1],
            from: index,
          });
        }
      }
    },
    { skip: !dragging, options: { passive: true } },
  );

  useEventListener(
    window,
    'mouseup',
    () => {
      setDragging(null);
      onRearrangingStateChange(null);
      if (rearrangingState) {
        onRearrange(index, rearrangingState.to, direction === 'row');
      }
    },
    { skip: !dragging },
  );

  return (
    <>
      <FakeSelectionButton
        ref={ref}
        data-no-print
        className={classNames(styles.control, styles[direction], {
          [styles.first]: index === 0,
          [styles.last]: last,
          [styles.prevSelected]: index === 0 ? false : getIsSelected(index - 1),
          [styles.selected]: getIsSelected(index),
          [styles.rearranging]: rearrangingState,
        })}
        onMouseDown={(e) => {
          if (!ref.current) return;
          const tableEl = ref.current?.closest('table');
          if (!tableEl) return;
          const rect = ref.current.getBoundingClientRect();
          setDragging({
            initialOffset:
              direction === 'row'
                ? rect.top - e.clientY
                : rect.left - e.clientX,
            style: {
              ...(direction === 'row'
                ? {
                    height: ref.current.clientHeight,
                    width: tableEl.clientWidth,
                  }
                : {
                    width: ref.current.clientWidth,
                    height: tableEl.clientHeight,
                  }),
            },
          });
        }}
        onClick={(event) => {
          onSelect(index, {
            isRow: direction === 'row',
            selectType: getSelectTypeFromClickEvent(event),
          });
        }}
        {...addTestId(
          direction === 'row' ? 'tableRowControl' : 'tableColumnControl',
        )}
      >
        <SVGIcon
          className={classNames(styles.dragIcon, styles[direction])}
          size={10}
        >
          {dragIcon}
        </SVGIcon>
      </FakeSelectionButton>
      {typeof dragging?.offset === 'number' && (
        <div
          className={classNames(styles.draggingSnapshot, styles[direction])}
          style={{
            ...dragging.style,
            [direction === 'row' ? 'top' : 'left']:
              dragging.offset + dragging.initialOffset,
          }}
        >
          <div className={classNames(styles.dragbar, styles[direction])}>
            <SVGIcon
              className={classNames(styles.dragIcon, styles[direction])}
              size={10}
            >
              {dragIcon}
            </SVGIcon>
          </div>
          <div className={styles.table} />
        </div>
      )}
    </>
  );
};

export default RowColumnControl;
